import React, { useState } from 'react';
import {
    Grid,
    Divider,
    TextField,
    Button,
    FormControl,
    FormControlLabel,
    Select,
    MenuItem,
    InputLabel,
    Checkbox,
    Typography,
    Box,
    Paper,
    Stack,
    Alert,
    Radio,
    RadioGroup,
} from '@mui/material';
import useToast from '../../../hooks/useToast';
import {
    useQuestionsContext,
    useSourceHelpers,
} from '../../../context/QuestionsContext';

const DEFAULT_MC_OPTIONS = [
    { id: 'a', text: '' },
    { id: 'b', text: '' },
    { id: 'c', text: '' },
    { id: 'd', text: '' },
];

export default function AddQuestion({ user }) {
    const toast = useToast();

    const {
        questions = [],
        sources = [],
        addQuestion,
        difficulties,
        questionTypes,
        loading,
        error,
    } = useQuestionsContext();

    const [formData, setFormData] = useState({
        text: '',
        questionType: 'TextInput',
        difficulty: 'Easy',
        correctAnswers: '',
        mcOptions: DEFAULT_MC_OPTIONS,
        mcCorrectAnswerId: null,
        explanation: '',
        hints: '',
        chapter: '',
        sourceType: 'Type',
        sourceId: '',
        needsReview: false,
    });

    const { uniqueSourceTypes, getSourcesByType } = useSourceHelpers();
    const filteredSourcesByType = getSourcesByType(formData.sourceType);

    const isMC = formData.questionType === 'MultipleChoice';

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleMcOptionTextChange = (id, value) => {
        setFormData((prev) => ({
            ...prev,
            mcOptions: prev.mcOptions.map((opt) =>
                opt.id === id ? { ...opt, text: value } : opt
            ),
        }));
    };

    const resetForm = () => {
        setFormData({
            text: '',
            questionType: 'TextInput',
            difficulty: '',
            correctAnswers: '',
            mcOptions: DEFAULT_MC_OPTIONS,
            mcCorrectAnswerId: 'a',
            explanation: '',
            hints: '',
            chapter: '',
            sourceId: '',
            isActive: true,
            isNew: true,
            needsReview: false,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!user || !user.userId) {
            toast.error('You must be logged in to add a question. Please log in first.');
            return;
        }
        // TODO: Add typing
        const submitData = {
            ...formData,
            mcOptions: isMC ? formData.mcOptions : null,
            correctAnswers: isMC
                ? [formData.mcCorrectAnswerId]
                : formData.correctAnswers
                    .split(',')
                    .map((a) => a.trim())
                    .filter(Boolean),
            hints: formData.hints
                ? formData.hints.split(',').map((h) => h.trim()).filter(Boolean)
                : [],
            sourceType: formData.sourceType || 'Unknown',
            questionType: formData.questionType || 'Unknown',
            sourceId: formData.sourceId ? parseInt(formData.sourceId) : 1,
            authorUserId: user.userId,
            questionId: window.crypto?.randomUUID?.() ?? generateUUID(),
            timesAnswered: 0,
            timesCorrect: 0,
            version: 1,
        };

        if (!formData.difficulty) {
            toast.error('Please select a difficulty level before saving.');
            return;
        }
        if (!formData.sourceId) {
            toast.error('Please select a source before saving.');
            return;
        }

        if (isMC) {
            if (!formData.mcCorrectAnswerId) {
                toast.error('Please select the correct answer option before saving.');
                return;
            }
            const emptyOptions = formData.mcOptions.filter((opt) => !opt.text.trim());
            if (emptyOptions.length > 0) {
                toast.error('Please fill in all four answer options before saving.');
                return;
            }
        }

        if (!isMC && !formData.correctAnswers.trim()) {
            toast.error('Please enter at least one correct answer before saving.');
            return;
        }

        try {
            await addQuestion(submitData);
            toast.success('New Question added successfully!');
            resetForm();
        } catch (err) {
            console.error('Error adding question:', err);
            const message = err?.response?.data?.message;
            toast.error(message || 'Failed to add new question. Please check all fields and try again.');
        }
    };

    function generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    if (!user || !user.userId) {
        return (
            <Paper sx={{ maxWidth: 800, mx: 'auto', p: 4 }}>
                <Alert severity='warning' sx={{ mb: 3 }}>
                    You must be logged in to add questions. Please log in to continue.
                </Alert>
                <Typography variant='h5' fontWeight='bold' color='text.secondary'>
                    Add New Question
                </Typography>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                    This feature is only available to authenticated users.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ maxWidth: 800, mx: 'auto', p: 4 }}>
            <Typography variant='h5' fontWeight='bold' gutterBottom>
                Add New Question
            </Typography>

            <Box component='form' onSubmit={handleSubmit} noValidate>
                <Stack spacing={3}>
                    {/* Question Text */}
                    <TextField
                        label='Question Text'
                        name='text'
                        value={formData.text}
                        onChange={handleChange}
                        required
                        multiline
                        rows={3}
                        fullWidth
                    />

                    {/* Question Type & Difficulty */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ minWidth: 200 }}>
                                <InputLabel>Question Type</InputLabel>
                                <Select
                                    name='questionType'
                                    value={formData.questionType}
                                    onChange={handleChange}
                                    label='Question Type'
                                >
                                    {questionTypes.map(({ id, label }) => (
                                        <MenuItem key={id} value={label}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ minWidth: 200 }}>
                                <InputLabel>Difficulty</InputLabel>
                                <Select
                                    name='difficulty'
                                    value={formData.difficulty}
                                    onChange={handleChange}
                                    label='Difficulty'
                                >
                                    {difficulties.map(({ id, label }) => (
                                        <MenuItem key={id} value={label}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* TextInput: freeform correct answers */}
                    {!isMC && (
                        <TextField
                            label='Correct Answer(s)'
                            name='correctAnswers'
                            value={formData.correctAnswers}
                            onChange={handleChange}
                            required
                            fullWidth
                            helperText='Separate multiple answer variations with commas (e.g., Lupin, Remus Lupin, Professor Lupin)'
                        />
                    )}

                    {/* MultipleChoice: options builder */}
                    {isMC && (
                        <Stack spacing={1}>
                            <Typography variant='subtitle2' color='text.secondary'>
                                Answer Options
                            </Typography>
                            <Divider />
                            <RadioGroup
                                value={formData.mcCorrectAnswerId}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        mcCorrectAnswerId: e.target.value,
                                    }))
                                }
                            >
                                {formData.mcOptions.map((opt) => (
                                    <Stack
                                        key={opt.id}
                                        direction='row'
                                        alignItems='center'
                                        spacing={1}
                                    >
                                        <FormControlLabel
                                            value={opt.id}
                                            control={<Radio size='small' />}
                                            label=''
                                            sx={{ mr: 0 }}
                                        />
                                        <Typography
                                            variant='body2'
                                            sx={{ minWidth: 20, fontWeight: 'bold' }}
                                        >
                                            {opt.id.toUpperCase()}.
                                        </Typography>
                                        <TextField
                                            size='small'
                                            fullWidth
                                            placeholder={`Option ${opt.id.toUpperCase()}`}
                                            value={opt.text}
                                            onChange={(e) =>
                                                handleMcOptionTextChange(opt.id, e.target.value)
                                            }
                                        />
                                    </Stack>
                                ))}
                            </RadioGroup>
                            {!formData.mcCorrectAnswerId && (
                                <Alert 
                                    severity='warning' 
                                    sx={{ 
                                        mt: 1,
                                        backgroundColor: 'rgba(255, 167, 38, 0.1)',
                                        color: '#ffa726',
                                        '& .MuiAlert-icon': {
                                            color: '#ffa726',
                                        }
                                    }}
                                >
                                    Select the radio button next to the correct answer before saving
                                </Alert>
                            )}
                        </Stack>
                    )}

                    {/* Explanation */}
                    <TextField
                        label='Explanation'
                        name='explanation'
                        value={formData.explanation}
                        onChange={handleChange}
                        multiline
                        rows={3}
                        fullWidth
                    />

                    {/* Hints */}
                    <TextField
                        label='Hints'
                        name='hints'
                        value={formData.hints}
                        onChange={handleChange}
                        fullWidth
                        helperText='Optional hints, separated by commas'
                    />

                    {/* Chapter & Source */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label='Chapter'
                                name='chapter'
                                value={formData.chapter}
                                onChange={handleChange}
                                fullWidth
                                placeholder='e.g., Chapter 17'
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth sx={{ minWidth: 150 }}>
                                <InputLabel>Source Type</InputLabel>
                                <Select
                                    label='Source Type'
                                    name='sourceType'
                                    value={formData.sourceType}
                                    onChange={handleChange}
                                    fullWidth
                                >
                                    {uniqueSourceTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth sx={{ mt: 2, minWidth: 150 }}>
                                <InputLabel>Source Title</InputLabel>
                                <Select
                                    label='Source Title'
                                    name='sourceId'
                                    value={formData.sourceId}
                                    onChange={handleChange}
                                    fullWidth
                                    disabled={!formData.sourceType}
                                >
                                    {filteredSourcesByType.map((source) => (
                                        <MenuItem key={source.id} value={source.id}>
                                            {source.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    {/* Checkboxes */}
                    <Stack direction='row' spacing={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.needsReview}
                                    onChange={handleChange}
                                    name='needsReview'
                                />
                            }
                            label='Needs Review'
                        />
                    </Stack>

                    {/* Buttons */}
                    <Stack direction='row' spacing={2} justifyContent='flex-end' pt={2}>
                        <Button
                            variant='outlined'
                            color='inherit'
                            type='button'
                            onClick={() => {
                                toast.info('Add question cancelled');
                                resetForm();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button variant='contained' color='primary' type='submit'>
                            Add Question
                        </Button>
                    </Stack>
                </Stack>
            </Box>
        </Paper>
    );
}